import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Input, Textarea } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { ImageGrid, ImagesBox, TextAreaBox } from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationStyles';

export interface ImageVariation {
  leftImageSrc: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  rightImageSrc: string;
  rightWatermarkTag: string;
  rightWatermarkPosition: string;
  text: string;
  brakes: boolean;
  divider: 'white' | 'black';
  fontSize?: number;
}

const StyledTextAreaBox = styled(TextAreaBox)`
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyledTextArea = styled(Textarea)`
  height: 90px;
`;

const StyledUploadedImages = styled(UploadedImages)`
  height: 360px;
`;

const StyledUploadMultipleImages = styled(UploadMultipleImages)`
  height: 209px;
`;

const AdditionalBox = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const RadioGroup = styled.div`
  width: 240px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Selection = styled.label`
  display: flex;
  border-radius: 4px;
  height: 24px;
  align-items: center;
  flex: 1;
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  gap: 9px;
`;

const Radio = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  position: relative;
  transition: border-color ease-out 0.2s;
`;

const RadioCircle = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  position: absolute;
  margin: auto;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  transition: background ease-out 0.2s;
`;

const RadioInput = styled.input`
  &:checked {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.purple};
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.purple};
      }
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:disabled {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.grayscale};
      cursor: not-allowed;
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.gray};
      }
      ${RadioCircle} {
        background: transparent;
      }
      ${Text} {
        color: ${({ theme }) => theme.colors.gray};
      }
    }
  }
  &:checked&:disabled {
    + ${Selection} {
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.gray};
      }
    }
  }
`;

interface Props {
  inArticle?: boolean;
}

const VariationMemeTrivia: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [brakes, setBrakes] = useState(false);
  const [fontSize, setFontSize] = useState('');
  const [divider, setDivider] = useState<'black' | 'white'>('white');

  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [rightImages, setRightImages] = useState<ImageData[]>([]);

  const [showLeftImagesModal, setShowLeftImagesModal] = useState(false);
  const [showRightImagesModal, setShowRightImagesModal] = useState(false);

  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setRightImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setFontSize('');
    setDivider('white');
  };

  const generateVariations = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const toSrc = (img: ImageData) =>
      img.croppedImage ?? URL.createObjectURL(img.image);

    const leftSrcs = leftImages.map(toSrc);
    const rightSrcs = rightImages.map(toSrc);

    const newVariations: ImageVariation[] = [];

    for (let leftIndex = 0; leftIndex < leftSrcs.length; leftIndex++) {
      const leftSrc = leftSrcs[leftIndex];
      for (let rightIndex = 0; rightIndex < rightSrcs.length; rightIndex++) {
        const rightSrc = rightSrcs[rightIndex];
        newVariations.push({
          leftImageSrc: leftSrc,
          leftWatermarkTag: getWatermarkTag(leftImages[leftIndex]),
          leftWatermarkPosition: getWatermarkPosition(leftImages[leftIndex]),
          rightImageSrc: rightSrc,
          rightWatermarkTag: getWatermarkTag(rightImages[rightIndex]),
          rightWatermarkPosition: getWatermarkPosition(rightImages[rightIndex]),
          text: text,
          fontSize: parseInt(fontSize),
          brakes,
          divider,
        });
      }
    }

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderRightImages = rightImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setRightImages}
    />
  ));

  const isResetDisabled =
    !leftImages?.length &&
    !text &&
    !brakes &&
    !rightImages?.length &&
    !fontSize &&
    divider === 'white';

  return (
    <>
      <VariationsStyles />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Meme</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach two half images and text with the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            <ImageGrid>
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Left Images</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderLeftImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowLeftImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
              <ImagesBox $hasImages={!!rightImages?.length} $hasLeftBorder>
                <LabelText $type="smallHeadline">Right Images</LabelText>
                {!!rightImages?.length ? (
                  <>
                    <StyledUploadedImages>
                      {renderRightImages}
                    </StyledUploadedImages>
                    <MoreImages>
                      <AttachedCountBadge count={rightImages.length} />
                      <AddMoreButton
                        onClick={() => setShowRightImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <StyledUploadMultipleImages
                    coverIndex={2}
                    noneDivider
                    setImages={setRightImages}
                  />
                )}
              </ImagesBox>
            </ImageGrid>
            <StyledTextAreaBox>
              <div>
                <LabelText $type="smallHeadline">Text</LabelText>
                <StyledTextArea
                  onChange={(e) => setText(e.target.value)}
                  value={text}
                  placeholder="Add text"
                ></StyledTextArea>
              </div>
              <div>
                <LabelText $type="smallHeadline">Font Size, px</LabelText>
                <Input
                  type="number"
                  min="0"
                  value={fontSize ?? ''}
                  onChange={(e) => setFontSize(e.target.value)}
                  placeholder="Add Font Size"
                />
              </div>
              <div>
                <ActionTitle $type="smallHeadline">Text Parameters</ActionTitle>
                <ActionBox>
                  <Checkbox
                    checked={brakes}
                    onChange={() => setBrakes((prev) => !prev)}
                  >
                    Custom brakes
                  </Checkbox>
                </ActionBox>
              </div>
            </StyledTextAreaBox>
            <AdditionalBox>
              <Text $type="smallHeadline" $color="gray">
                Additional:
              </Text>
              <RadioGroup>
                <RadioInput
                  checked={divider === 'white'}
                  name="divider"
                  type="radio"
                  id="white-divider"
                  hidden
                  onChange={() => setDivider('white')}
                />
                <Selection htmlFor="white-divider">
                  <Radio>
                    <RadioCircle />
                  </Radio>
                  <Text $type="smallBold">White Divider</Text>
                </Selection>
                <RadioInput
                  checked={divider === 'black'}
                  name="divider"
                  type="radio"
                  id="black-divider"
                  hidden
                  onChange={() => setDivider('black')}
                />
                <Selection htmlFor="black-divider">
                  <Radio>
                    <RadioCircle />
                  </Radio>
                  <Text $type="smallBold">Black Divider</Text>
                </Selection>
              </RadioGroup>
            </AdditionalBox>
          </>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button
            disabled={!leftImages?.length || !rightImages?.length}
            onClick={generateVariations}
          >
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showLeftImagesModal && (
          <MoreImagesModal
            onClose={() => setShowLeftImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
          />
        )}
        {!!showRightImagesModal && (
          <MoreImagesModal
            onClose={() => setShowRightImagesModal(false)}
            setImages={setRightImages}
            coverIndex={2}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationMemeTrivia;
